import { useEffect, useState } from "react";

import { messages } from "../../lib/messages";
import { cartQtyOf, useCart } from "../cart/useCart";
import { useSetCartItem } from "../cart/useSetCartItem";
import type { ProductSummary } from "../../api/catalog";

interface ProductCardProps {
  product: ProductSummary;
  onOpen(productId: string): void;
}

/**
 * ProductCard — карточка товара в сетке каталога (docs/07-miniapp-ux.md §2.2): обложка,
 * бейдж скидки, быстрое добавление «+» → «−1+» прямо в карточке, без перехода в товар.
 * Количество меняется мгновенно (локальный оптимистичный qty), а сетевой PUT уходит
 * с debounce (useSetCartItem) — быстрые тапы не должны рождать запрос на каждый.
 */
export function ProductCard({ product, onOpen }: ProductCardProps) {
  const cart = useCart();
  const setCartItem = useSetCartItem();
  const serverQty = cartQtyOf(cart.data, product.id);
  const [optimisticQty, setOptimisticQty] = useState<number | null>(null);

  // Сервер прислал новое значение (наш же запрос доехал, или пришли из карточки
  // товара) — локальная догадка больше не нужна.
  useEffect(() => {
    setOptimisticQty(null);
  }, [serverQty]);

  const qty = optimisticQty ?? serverQty;

  function change(next: number) {
    setOptimisticQty(Math.max(0, next));
    setCartItem(product.id, Math.max(0, next));
  }

  return (
    <div className="flex flex-col overflow-hidden rounded-card bg-surface">
      <button type="button" onClick={() => onOpen(product.id)} className="flex flex-col text-left">
        <div className="relative aspect-square bg-bg">
          {product.image_url ? (
            <img src={product.image_url} alt={product.title} loading="lazy" className="h-full w-full object-cover" />
          ) : null}
          {product.old_price !== null ? (
            <span className="absolute left-2 top-2 rounded-full bg-accent px-2 py-0.5 text-xs text-button-text">
              -{product.discount_percent}%
            </span>
          ) : null}
        </div>
        <div className="flex flex-col gap-1 p-2">
          <p className={`line-clamp-2 text-sm ${product.available ? "text-text" : "text-hint"}`}>{product.title}</p>
          <div className="flex items-baseline gap-1.5">
            <span className={product.available ? "font-semibold text-text" : "text-hint"}>{product.price_text}</span>
            {product.old_price_text ? <span className="text-xs text-hint line-through">{product.old_price_text}</span> : null}
          </div>
        </div>
      </button>
      <div className="px-2 pb-2">
        {!product.available ? (
          <p className="text-center text-sm text-hint">{messages.outOfStock}</p>
        ) : qty === 0 ? (
          <button
            type="button"
            aria-label="Добавить"
            onClick={() => change(qty + 1)}
            className="w-full rounded-card bg-button py-1.5 text-button-text"
          >
            +
          </button>
        ) : (
          <div className="flex items-center justify-between rounded-card bg-button text-button-text">
            <button type="button" aria-label="Убавить" onClick={() => change(qty - 1)} className="px-3 py-1.5">
              −
            </button>
            <span>{qty}</span>
            <button type="button" aria-label="Добавить" onClick={() => change(qty + 1)} className="px-3 py-1.5">
              +
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
